<template>
    <button
        data-testid="bet-slip-cta"
        class="betSlipCta"
        v-bind:class="[
            {
                'betSlipCta-updateStake': usesUpdateStakeStyle,
                'betSlipCta-processing': loading,
            },
            gaClasses,
        ]"
        v-bind:data-ticket-count="ticketCount"
        type="button"
        v-bind:disabled="disabled || loading"
        v-on:click="emit('click')"
    >
        {{ label }}
    </button>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { BetSlipCtaAction } from '@/interface/enum';
    import { i18n } from '@/libraries/i18n';

    const props = withDefaults(defineProps<{
        ctaAction: BetSlipCtaAction;
        disabled: boolean;
        loading: boolean;
        ticketCount: number;
        gaClassPrefix?: string;
    }>(), {
        gaClassPrefix: '',
    });

    const emit = defineEmits<{
        click: [];
    }>();

    const label = computed(() => getCtaLabel(props.ctaAction));
    const usesUpdateStakeStyle = computed(() => (
        props.ctaAction === BetSlipCtaAction.UpdateStake
        || props.ctaAction === BetSlipCtaAction.AcceptChange
    ));
    const gaEventSuffix = computed(() => getGaEventSuffix(props.ctaAction));
    const gaClasses = computed(() => {
        if (gaEventSuffix.value === '') return [];

        const clickClass = `${props.gaClassPrefix}_${gaEventSuffix.value}`;
        if (props.ctaAction === BetSlipCtaAction.PlaceBet) return [clickClass];

        return [clickClass, `${clickClass}_View`];
    });

    function getGaEventSuffix(ctaAction: BetSlipCtaAction): string {
        switch (ctaAction) {
            case BetSlipCtaAction.AcceptChange:
                return 'Btn_AcceptNewOdd';
            case BetSlipCtaAction.UpdateStake:
                return 'Btn_UpdateStake';
            case BetSlipCtaAction.PlaceBet:
                return 'Btn_PlaceBet';
            default:
                return '';
        }
    }

    function getCtaLabel(ctaAction: BetSlipCtaAction): string {
        switch (ctaAction) {
            case BetSlipCtaAction.UpdateStake:
                return i18n.t('bet_slip_cta_update_stake');
            case BetSlipCtaAction.AcceptChange:
                return i18n.t('bet_slip_cta_accept_new_odds');
            case BetSlipCtaAction.PlaceBet:
                return i18n.t('placeBet_button_placeBet');
            case BetSlipCtaAction.Deposit:
                return i18n.t('comm_deposit_button');
            default: {
                const unsupportedAction: never = ctaAction;
                throw new Error(`Unsupported BetSlip CTA action: ${unsupportedAction}`);
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "@/scss/components/singleMode/betSlipCta";
</style>
